<template>
  <AuthShell>
    <v-form v-if="!secondVerificationStep" class="auth-form" @submit.prevent="loginUser">
      <h1 class="auth-form__title">Welcome back</h1>

      <div class="auth-form__field">
        <label for="signin-email" class="auth-form__label">Email address</label>
        <v-text-field
          id="signin-email"
          v-model="credentials.email"
          type="email"
          placeholder="you@clinic.com"
          autocomplete="username"
          :error-messages="!isEmailValid && formButtonClicked ? 'Please enter a valid email address' : ''" />
      </div>

      <div class="auth-form__field">
        <label for="signin-password" class="auth-form__label">Password</label>
        <v-text-field
          id="signin-password"
          v-model="credentials.password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="current-password"
          :error-messages="getPasswordErrorMessage"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPassword = !showPassword" />
      </div>

      <div class="auth-form__row">
        <router-link :to="{ name: 'password-reset-request' }" class="auth-form__link">Forgot password?</router-link>
      </div>

      <v-btn color="primary" block size="large" type="submit" :loading="showPreloader">Sign in</v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>

      <p class="auth-form__center">New here? Ask your admin for an invite.</p>
    </v-form>

    <v-form v-else class="auth-form" @submit.prevent="verifyOtp">
      <h1 class="auth-form__title">Check your email</h1>
      <p class="auth-form__sub">
        We sent a 6-digit code to <b>{{ maskedEmail }}</b
        >. It expires in 5 minutes.
      </p>

      <v-otp-input v-model="otpCode" length="6" color="surface" :disabled="showPreloader" />

      <v-btn
        color="primary"
        block
        size="large"
        type="submit"
        :loading="showPreloader"
        :disabled="!otpCode || otpCode.length !== 6">
        Verify code
      </v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>

      <p class="auth-form__center">Didn't get it? Check your spam folder, or ask your admin to resend.</p>
    </v-form>
  </AuthShell>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import { mainServices } from "@/services/mainServices";
import AuthShell from "@/components/AuthShell.vue";

const secondVerificationStep = ref(false);
const otpCode = ref("");
const errors = ref([]);
const formButtonClicked = ref(false);
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const showPreloader = ref(false);
const showPassword = ref(false);

// Add watcher for otpCode
watch(otpCode, (newValue) => {
  // Automatically submit when OTP is fully entered (6 digits)
  if (newValue && newValue.length === 6) {
    verifyOtp();
  }
});

const redirectAfterLogin = async () => {
  const redirectPath = route.query.redirect;
  if (redirectPath) {
    router.push(redirectPath);
    return;
  }
  try {
    const config = await mainServices.getCurrentUserClientConfig();
    const page = config?.uiConfig?.defaultPostLoginPage || "all-submissions";
    router.push({ name: page });
  } catch {
    router.push({ name: "all-submissions" });
  }
};

const credentials = ref({
  email: "",
  password: "",
});

// Validators
const isEmailValid = computed(() => {
  const email = credentials.value.email;
  return email !== "" && FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
});

const isPasswordValid = computed(() => {
  const password = credentials.value.password;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

/** Shows the user which inbox to check without printing the whole address. */
const maskedEmail = computed(() => {
  const [name, domain] = credentials.value.email.split("@");
  if (!domain) return "your email";
  return `${name.slice(0, 1)}•••@${domain}`;
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!credentials.value.password) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const loginUser = async () => {
  formButtonClicked.value = true;
  if (isEmailValid.value && isPasswordValid.value) {
    showPreloader.value = true;
    try {
      let otpConfig = await authStore.login(credentials.value);
      if (otpConfig.secondStep) {
        secondVerificationStep.value = true;
      } else {
        await authStore.loginStep2NotRequired();
        await redirectAfterLogin();
      }
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    }
    showPreloader.value = false;
  }
};

const verifyOtp = async () => {
  if (!otpCode.value || otpCode.value.length !== 6) return;

  showPreloader.value = true;
  try {
    await authStore.loginStep2(otpCode.value);
    await redirectAfterLogin();
    secondVerificationStep.value = false;
    otpCode.value = ""; // Clear OTP after successful verification
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    showPreloader.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_auth-form.scss";
</style>
